import React, { useEffect } from 'react';

import { Select as AntdSelect, SelectProps as AntdSelectProps } from 'antd';
import { NamePath as AntdNamePath } from 'antd/lib/form/interface';
import { DefaultOptionType } from 'antd/lib/select';
import { RawValueType } from 'rc-select/lib/BaseSelect';
import { LabelInValueType } from 'rc-select/lib/Select';

import { getStringOrNullOrThrow } from 'typeGuards/typeGuards';
import { FormPlaceholders } from 'types/enums/FormPlaceholders';

import { useBaseFormContext } from '../BaseFormContext';
import { filterOption, setFieldValue } from '../utils';

import './CommonBaseSelect.less';

export interface BaseOptionType<OptionType> extends DefaultOptionType {
  option?: OptionType;
}

export interface BaseSelectDependencies<FormValues, OptionType> {
  name: keyof FormValues & AntdNamePath & string;
  getValue: (option: OptionType) => unknown;
}

export interface CommonBaseSelectProps<FormValues, OptionType>
  extends AntdSelectProps {
  dependencies?: BaseSelectDependencies<FormValues, OptionType>[];
  autoSelectSingleOption?: boolean;
  disableWhenSingleOption?: boolean;
}

const computeSingleOptionValueAndDisabledOverride = <
  OptionType extends DefaultOptionType,
>(
  options: OptionType[] | undefined,
  autoSelectSingleOption: boolean,
  disableWhenSingleOption: boolean,
  defaultDisabledValue?: boolean,
): { singleOptionValue?: string | number | null; disabled?: boolean } => {
  const isSingleOption = options?.length === 1;
  const singleOption =
    autoSelectSingleOption && isSingleOption ? options[0] : undefined;
  const singleOptionValue = singleOption?.value;

  const disabled =
    singleOptionValue && disableWhenSingleOption ? true : defaultDisabledValue;

  return { singleOptionValue, disabled };
};

export default function CommonBaseSelect<FormValues, OptionType>({
  dependencies,
  autoSelectSingleOption,
  disableWhenSingleOption,
  ...props
}: CommonBaseSelectProps<FormValues, OptionType>): React.ReactElement {
  const form = useBaseFormContext<FormValues>();
  const { singleOptionValue, disabled } =
    computeSingleOptionValueAndDisabledOverride(
      props.options,
      !!autoSelectSingleOption,
      !!disableWhenSingleOption,
      props.disabled,
    );

  useEffect(() => {
    if (singleOptionValue) {
      setFieldValue(form, props.id, singleOptionValue);
    }
  }, [singleOptionValue]);

  return (
    <AntdSelect
      placeholder={FormPlaceholders.Select}
      filterOption={(input: string, option?: BaseOptionType<OptionType>) => {
        if (option?.label) {
          return filterOption(input, option.label as string);
        }
        return false;
      }}
      onSelect={(
        value: RawValueType | LabelInValueType,
        option: BaseOptionType<OptionType>,
      ) => {
        props.onSelect && props.onSelect(value, option);
        const selectedValue = option.option;

        if (selectedValue) {
          dependencies?.forEach(dependency => {
            setFieldValue(
              form,
              dependency.name,
              dependency.getValue(selectedValue),
            );
          });

          form
            .validateFields(
              dependencies?.map(dependency =>
                getStringOrNullOrThrow(dependency.name),
              ) ?? [],
            )
            .catch(() => {
              // Validation error occurred, do nothing
            });
        }
      }}
      {...props}
      disabled={disabled}
    />
  );
}
